import React, { useState } from 'react';
import { X, Database, BarChart3, Calculator, Search, ChevronDown, ChevronRight, BookOpen, Copy, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Data definitions - can be moved to a separate config file later
const DATA_DICTIONARY = {
  dimensions: [
    { name: 'Location', field: 'location', description: 'Physical work location or property (e.g., Bayfront Resort Miami)' },
    { name: 'Department', field: 'department', description: 'Cost center or department (e.g., F&B, Housekeeping, Front Desk)' },
    { name: 'Supervisor', field: 'supervisor', description: 'Direct supervisor or manager name' },
    { name: 'Employee', field: 'employee', description: 'Individual employee (identified by Employee Number)' },
    { name: 'Job Title', field: 'job_title', description: 'Employee job title or position' },
    { name: 'Pay Type', field: 'pay_type', description: 'Type of pay (Regular, Overtime, Double Time, PTO, etc.)' },
    { name: 'Date', field: 'date', description: 'Date of the timesheet entry' },
    { name: 'Time Period', field: 'period', description: 'Week, Month, Quarter, or Year for aggregation' }
  ],
  measures: [
    { 
      name: 'Total Hours', 
      field: 'total_hours', 
      description: 'Sum of all hours worked across all pay types',
      formula: 'SUM(Hours)',
      example: '1,250.5 hours'
    },
    { 
      name: 'Premium Hours', 
      field: 'premium_hours', 
      description: 'Hours paid at premium rates (Overtime, Double Time, Holiday, etc.)',
      formula: 'SUM(Hours) WHERE Pay Type IN (Overtime, Double Time, Holiday, ...)',
      example: '187.3 hours'
    },
    { 
      name: 'Non-Premium Hours', 
      field: 'non_premium_hours', 
      description: 'Regular hours not subject to premium pay',
      formula: 'Total Hours - Premium Hours',
      example: '1,063.2 hours'
    },
    { 
      name: 'Premium Percent', 
      field: 'premium_percent', 
      description: 'Percentage of total hours that are premium',
      formula: '(Premium Hours / Total Hours) × 100',
      example: '15.0%'
    },
    { 
      name: 'Headcount', 
      field: 'employee_count', 
      description: 'Number of unique employees',
      formula: 'COUNT(DISTINCT Employee Number)',
      example: '47 employees'
    },
    { 
      name: 'Estimated Cost', 
      field: 'estimated_cost', 
      description: 'Estimated labor cost including benefits overhead',
      formula: 'Total Hours × Avg Hourly Rate × 1.3 (30% benefits factor)',
      example: '$45,230.00'
    },
    { 
      name: 'Avg Hourly Rate', 
      field: 'hourly_rate', 
      description: 'Average hourly pay rate for employees',
      formula: 'AVG(Hourly Pay Rate)',
      example: '$18.50/hr'
    },
    { 
      name: 'Cost per Premium Hour', 
      field: 'cost_per_premium_hour', 
      description: 'Average cost for each premium hour worked',
      formula: 'Premium Cost / Premium Hours',
      example: '$32.75/hr'
    }
  ],
  formulas: [
    {
      name: 'Premium Pay Classification',
      description: 'How pay types are classified as premium vs non-premium',
      formula: `Premium Pay Types:
  • Overtime (OT)
  • Double Time (DT)
  • Holiday Pay
  • Call-In Pay
  • Shift Differentials (SHIFT2, SHIFT3)
  
Non-Premium Pay Types:
  • Regular (REG)
  • PTO/Vacation
  • Sick Leave
  • Training`,
      notes: 'Premium classification can be customized in Settings > Pay Type Configuration'
    },
    {
      name: 'Estimated Cost Calculation',
      description: 'How labor costs are estimated from hours',
      formula: `Estimated Cost = Total Hours × Hourly Rate × 1.3

Where:
  • Total Hours = Sum of all hours worked
  • Hourly Rate = Employee's base hourly pay rate
  • 1.3 = Benefits multiplier (30% overhead for benefits, taxes, etc.)`,
      notes: 'Premium hours may have higher effective rates (1.5x for OT, 2x for DT)'
    },
    {
      name: 'Overtime Efficiency Score',
      description: 'Measures how effectively overtime is being managed',
      formula: `Efficiency Score = 100 - (Premium % × Weight Factor)

Where:
  • Premium % = (Premium Hours / Total Hours) × 100
  • Weight Factor varies by industry benchmark
  
Interpretation:
  • 90-100: Excellent - minimal premium hours
  • 75-89: Good - acceptable premium usage
  • 60-74: Fair - room for improvement
  • Below 60: Needs attention`,
      notes: 'Industry benchmarks vary; hospitality typically targets 10-15% premium hours'
    },
    {
      name: 'YoY Comparison',
      description: 'Year-over-year change calculation',
      formula: `YoY Change % = ((Current Year Value - Prior Year Value) / Prior Year Value) × 100

Example:
  • 2024 Premium Hours: 5,000
  • 2023 Premium Hours: 4,500
  • YoY Change: ((5000 - 4500) / 4500) × 100 = +11.1%`,
      notes: 'Positive values indicate increase; negative values indicate decrease'
    }
  ]
};

const DataDictionary = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('dimensions');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [copiedFormula, setCopiedFormula] = useState(null);

  const toggleExpand = (itemName) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemName]: !prev[itemName]
    }));
  };

  const copyFormula = async (formula, name) => {
    try {
      await navigator.clipboard.writeText(formula);
      setCopiedFormula(name);
      setTimeout(() => setCopiedFormula(null), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  const filterItems = (items) => {
    if (!searchQuery) return items;
    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      item.name.toLowerCase().includes(query) ||
      item.description.toLowerCase().includes(query) ||
      (item.formula && item.formula.toLowerCase().includes(query))
    );
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[85vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#1e3a5f] to-[#2E5C8A] text-white p-6">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-3">
                  <BookOpen className="h-7 w-7" />
                  Data Dictionary
                </h2>
                <p className="text-white/80 mt-1">Available dimensions, measures, and formulas for analysis</p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Search */}
            <div className="mt-4 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/60" />
              <input
                type="text"
                placeholder="Search dimensions, measures, or formulas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/20 text-white placeholder-white/60 rounded-lg pl-10 pr-4 py-2 border border-white/30 focus:outline-none focus:border-white/60"
              />
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mt-4">
              {[
                { id: 'dimensions', label: 'Dimensions', icon: Database, count: DATA_DICTIONARY.dimensions.length },
                { id: 'measures', label: 'Measures', icon: BarChart3, count: DATA_DICTIONARY.measures.length },
                { id: 'formulas', label: 'Formulas', icon: Calculator, count: DATA_DICTIONARY.formulas.length }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-white text-[#1e3a5f] font-medium' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    activeTab === tab.id ? 'bg-[#1e3a5f]/10' : 'bg-white/20'
                  }`}>
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(85vh-220px)]">
            {activeTab === 'dimensions' && (
              <div className="space-y-3">
                {filterItems(DATA_DICTIONARY.dimensions).map(dim => (
                  <div 
                    key={dim.field}
                    className="bg-slate-50 rounded-lg p-4 border border-slate-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                        <Database className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-800">{dim.name}</h4>
                        <p className="text-sm text-slate-600">{dim.description}</p>
                        <code className="text-xs text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded mt-1 inline-block">
                          field: {dim.field}
                        </code>
                      </div>
                    </div>
                  </div>
                ))}
                {filterItems(DATA_DICTIONARY.dimensions).length === 0 && (
                  <p className="text-center text-slate-500 py-8">No dimensions match your search</p>
                )}
              </div>
            )}

            {activeTab === 'measures' && (
              <div className="space-y-3">
                {filterItems(DATA_DICTIONARY.measures).map(measure => (
                  <div 
                    key={measure.field}
                    className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden"
                  >
                    <button
                      onClick={() => toggleExpand(measure.field)}
                      className="w-full p-4 flex items-center justify-between hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                          <BarChart3 className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="text-left">
                          <h4 className="font-semibold text-slate-800">{measure.name}</h4>
                          <p className="text-sm text-slate-600">{measure.description}</p>
                        </div>
                      </div>
                      {expandedItems[measure.field] ? (
                        <ChevronDown className="h-5 w-5 text-slate-400" />
                      ) : (
                        <ChevronRight className="h-5 w-5 text-slate-400" />
                      )}
                    </button>
                    
                    <AnimatePresence>
                      {expandedItems[measure.field] && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="border-t border-slate-200 bg-white"
                        >
                          <div className="p-4 space-y-3">
                            <div>
                              <span className="text-xs font-medium text-slate-500 uppercase">Formula</span>
                              <div className="flex items-center gap-2 mt-1">
                                <code className="flex-1 text-sm bg-slate-100 px-3 py-2 rounded font-mono text-slate-700">
                                  {measure.formula}
                                </code>
                                <button
                                  onClick={() => copyFormula(measure.formula, measure.field)}
                                  className="p-2 hover:bg-slate-100 rounded transition-colors"
                                  title="Copy formula"
                                >
                                  {copiedFormula === measure.field ? (
                                    <Check className="h-4 w-4 text-green-500" />
                                  ) : (
                                    <Copy className="h-4 w-4 text-slate-400" />
                                  )}
                                </button>
                              </div>
                            </div>
                            <div>
                              <span className="text-xs font-medium text-slate-500 uppercase">Example Value</span>
                              <p className="text-sm text-slate-700 mt-1">{measure.example}</p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
                {filterItems(DATA_DICTIONARY.measures).length === 0 && (
                  <p className="text-center text-slate-500 py-8">No measures match your search</p>
                )}
              </div>
            )}

            {activeTab === 'formulas' && (
              <div className="space-y-4">
                {filterItems(DATA_DICTIONARY.formulas).map(formula => (
                  <div 
                    key={formula.name}
                    className="bg-slate-50 rounded-lg border border-slate-200 overflow-hidden"
                  >
                    <div className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Calculator className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-800">{formula.name}</h4>
                          <p className="text-sm text-slate-600">{formula.description}</p>
                        </div>
                      </div>
                      
                      <div className="bg-slate-900 rounded-lg p-4 mt-3">
                        <div className="flex justify-between items-start mb-2">
                          <span className="text-xs font-medium text-slate-400 uppercase">Formula / Logic</span>
                          <button
                            onClick={() => copyFormula(formula.formula, formula.name)}
                            className="p-1 hover:bg-slate-700 rounded transition-colors"
                            title="Copy formula"
                          >
                            {copiedFormula === formula.name ? (
                              <Check className="h-4 w-4 text-green-400" />
                            ) : (
                              <Copy className="h-4 w-4 text-slate-400" />
                            )}
                          </button>
                        </div>
                        <pre className="text-sm text-green-400 font-mono whitespace-pre-wrap">
                          {formula.formula}
                        </pre>
                      </div>
                      
                      {formula.notes && (
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <span className="text-xs font-medium text-amber-700 uppercase">Note</span>
                          <p className="text-sm text-amber-800 mt-1">{formula.notes}</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {filterItems(DATA_DICTIONARY.formulas).length === 0 && (
                  <p className="text-center text-slate-500 py-8">No formulas match your search</p>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
            <p className="text-xs text-slate-500 text-center">
              Use these dimensions and measures in AI Chat to ask specific questions about your data.
              Premium pay type classifications can be customized in Settings.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DataDictionary;
